import {
  filterExistsAndExcludes,
  StrategyScopeEnum,
  useAccordionFilterBuilder,
  useAssetsFilter,
  useConstant,
  useCurrenciesFilter,
  useGenericFilter,
  useWSFilterPipe,
  type FilterableProperty,
  type GenericFilterFilterProps,
  type IHedgeRule,
  type MinimalSubscriptionResponse,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { keys } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { ReplaySubject } from 'rxjs';
import { useIDFilter, useModeFilter, useStrategiesFilter, useSubAccountsFilter } from '../../../../hooks';
import { useRollupsFilter } from '../../../../hooks/filters/useRollupsFilter';
import { useSubAccounts } from '../../../../providers';
import { useHedgeRulesQuery } from './queries';

export function usePositionAutoHedgingRules({
  onFilterChanged: saveFilter,
  initialState,
}: Pick<UsePersistedBlotterTable, 'initialState' | 'onFilterChanged'>) {
  const hedgeRulesQuery = useHedgeRulesQuery();
  const replaySub = useConstant(new ReplaySubject<MinimalSubscriptionResponse<IHedgeRule>>());
  useEffect(() => {
    if (hedgeRulesQuery.data) {
      replaySub.next({
        data: hedgeRulesQuery.data.data,
        initial: true,
      });
    }
  }, [hedgeRulesQuery.data, replaySub]);

  const assetFilter = useAssetsFilter();
  const currencyFilter = useCurrenciesFilter();
  const subaccountFilter = useSubAccountsFilter({ includeRollups: false });
  const rollupsFilter = useRollupsFilter();
  const idFilter = useIDFilter();
  const modeFilter = useModeFilter();
  const strategiesFilter = useStrategiesFilter({ strategyScope: StrategyScopeEnum.AutoHedgingStrategy });
  const { subAccountsByID } = useSubAccounts();

  const filterableProperties = useMemo(
    () =>
      [
        { ...assetFilter, label: 'Monitored Asset', key: 'Asset', field: 'Asset' } as const,
        { ...currencyFilter, label: 'Target Asset', key: 'TargetAsset', field: 'TargetAsset' } as const,
        { ...rollupsFilter, label: 'Monitored Rollup', key: 'AccountID', field: 'AccountID' } as const,
        { ...subaccountFilter, label: 'Hedge Account', key: 'TargetAccountID', field: 'TargetAccountID' } as const,
        { ...idFilter, key: 'HedgeRuleID', field: 'HedgeRuleID', label: 'Hedge Rule ID' } as const,
        { ...modeFilter, key: 'Mode', field: 'Mode' } as const,
        { ...strategiesFilter, label: 'Hedge Strategy', key: 'HedgeStrategy', field: 'HedgeStrategy' } as const,
      ] satisfies FilterableProperty<keyof IHedgeRule>[],
    [assetFilter, currencyFilter, subaccountFilter, idFilter, modeFilter, strategiesFilter, rollupsFilter]
  );

  const filterProps: GenericFilterFilterProps = useMemo(
    () => ({
      initialFilter: initialState?.filter,
      onFilterChanged: saveFilter,
    }),
    [initialState?.filter, saveFilter]
  );
  const filterResults = useGenericFilter(filterableProperties, filterProps);

  const rxjsFilterFunc = useCallback(
    (data: IHedgeRule) => {
      let filteredOut = false;

      filterableProperties.forEach(property => {
        if (property.field === 'AccountID' || property.field === 'TargetAccountID') {
          // need to convert the subaccount name to the subaccount id
          const subAccountName = subAccountsByID.get(data[property.field])?.Name;
          if (
            subAccountName &&
            filterExistsAndExcludes(
              filterResults.filter,
              property.key,
              { [property.field]: subAccountName },
              property.field
            )
          ) {
            filteredOut = true;
          }
        } else {
          if (filterExistsAndExcludes(filterResults.filter, property.key, data, property.field)) {
            filteredOut = true;
          }
        }
      });
      return !filteredOut;
    },
    [filterResults.filter, filterableProperties, subAccountsByID]
  );

  const filterPipe = useWSFilterPipe({
    getUniqueKey: row => row.HedgeRuleID,
    filterFunc: rxjsFilterFunc,
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: keys(filterResults.filter).length > 0 },
    filterBuilderProps: filterResults.filterBuilderProps,
  });

  return useMemo(
    () => ({
      dataObs: replaySub.pipe(filterPipe),
      filterBuilderAccordion,
    }),
    [filterBuilderAccordion, filterPipe, replaySub]
  );
}
